import React, { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAlert } from '~/components/Alerts/alertsStore';

import { useBrandInfo } from './useBrandInfo';

type ErrorMessage = {
  i18nKey: string;
  values?: Record<string, string>;
  components?: Record<string, React.ReactElement>;
};

const strongComponent = { strong: React.createElement('strong') };

const errorMessages: Record<string, ErrorMessage> = {
  domainInUse: {
    i18nKey: 'domainInUse',
    components: strongComponent,
  },
  domainInUseByOtherAccount: { i18nKey: 'domainInUseByOtherAccount' },
  domainReserved: { i18nKey: 'domainReserved' },
  siteLimitExceeded: { i18nKey: 'siteLimitExceeded' },
  domainLimitExceeded: { i18nKey: 'domainLimitExceeded' },
  parkedDomainLimitExceeded: { i18nKey: 'parkedDomainLimitExceeded' },
  addonDomainLimitExceeded: { i18nKey: 'addonDomainLimitExceeded' },
  subDomainLimitExceeded: { i18nKey: 'subDomainLimitExceeded' },
  docRootLimitExceeded: { i18nKey: 'docRootLimitExceeded' },
  dbLimitExceeded: { i18nKey: 'dbLimitExceeded' },
  diskLimitExceeded: { i18nKey: 'diskLimitExceeded' },
  cpuWorkersLimitExceeded: { i18nKey: 'cpuWorkersLimitExceeded' },
  accountLimitExceeded: { i18nKey: 'accountLimitExceeded' },
  databaseLimitExceeded: { i18nKey: 'databaseLimitExceeded' },
  domainAddError: { i18nKey: 'domainAddError' },
  domainInvalid: { i18nKey: 'domainInvalid' },
  domainAlreadyAdded: { i18nKey: 'domainAlreadyAdded' },
  pendingUrlNotFound: { i18nKey: 'pendingUrlNotFound' },
  domainAlreadyAssigned: { i18nKey: 'domainAlreadyAssigned' },
  domainAlreadyExists: { i18nKey: 'domainAlreadyExists' },
  authoritativeDomainExists: {
    i18nKey: 'existingDomainAddError',
    components: strongComponent,
  },
  domainBrandMismatch: {
    i18nKey: 'domainBrandMismatch',
  },
};

export const useErrorHandler = () => {
  const { t } = useTranslation('errors');
  const navigate = useNavigate();
  const generateAlert = useAlert();
  const { phoneNumberFormatted } = useBrandInfo();

  const setAlertMessage = useCallback(
    (severity: string, description: string | JSX.Element, persist = false) => {
      generateAlert({
        severity,
        description,
        showCloseBtn: true,
        persist,
      });
    },
    [generateAlert],
  );

  const handleErrors = useCallback(
    (error: any) => {
      const errorCode = error?.response?.data?.error;

      if (
        errorCode === 'domainInUseForceVhostAvailable' ||
        errorCode === 'domainAlreadyAssigned'
      ) {
        navigate('reassign');
        return;
      }

      const message = errorMessages[errorCode];

      if (message) {
        const { i18nKey, values = {}, components = {} } = message;
        let valuesToUse = values;
        if (errorCode === 'domainBrandMismatch') {
          valuesToUse = { ...values, Support_Phone: phoneNumberFormatted };
        }
        setAlertMessage(
          'error',
          React.createElement(Trans, {
            t,
            i18nKey,
            values: valuesToUse,
            components,
          }),
          true,
        );
      } else {
        setAlertMessage('error', t('alertError'), true);
        navigate('connect');
      }
    },
    [navigate, phoneNumberFormatted, setAlertMessage, t],
  );

  return { handleErrors };
};
